import { useEffect, useRef, useState } from 'react';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';

const DEFAULT_PAGE_SIZE = 10;

const useCrmInteractionsQuery = ({
  filters,
  sort,
  query,
  pageSize = DEFAULT_PAGE_SIZE,
  dependencyKey,
  enabled = true,
  cachedResult,
  skipInitialFetch,
  onResultChange,
}) => {
  const hasUsableCache = skipInitialFetch && cachedResult?.hits?.length > 0;
  const [isLoading, setIsLoading] = useState(!hasUsableCache);
  const [interactions, setInteractions] = useState(hasUsableCache ? cachedResult.hits : null);
  const [thereIsMoreContent, setThereIsMoreContent] = useState(hasUsableCache ? cachedResult.thereIsMoreContent : false);
  const [isLoadingScroll, setIsLoadingScroll] = useState(false);

  const { generalApiCall } = useGeneralApiCall();

  const pageRef = useRef(hasUsableCache ? Math.max(1, Math.ceil((cachedResult.hits?.length ?? 0) / pageSize)) : 1);
  const hasUsableCacheRef = useRef(hasUsableCache);
  hasUsableCacheRef.current = hasUsableCache;
  const loadMoreSourceRef = useRef(null);
  const initialCall = useRef(null);
  const loadMoreCall = useRef(null);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const queryRef = useRef(query);
  queryRef.current = query;

  initialCall.current = async (source) => {
    setIsLoading(true);
    const requestProperties = { pageNumber: 1, pageSize };
    if (sort) requestProperties.sort = sort;
    if (filtersRef.current?.length) requestProperties.filters = filtersRef.current;
    if (queryRef.current) requestProperties.query = queryRef.current;
    const result = await generalApiCall({
      method: 'post',
      pathname: '/api/crm-interaction/query',
      requestProperties,
      needsAuthentication: true,
      requestSource: source,
      notShowErrorMessage: true,
    });
    if (source.signal.aborted) return;
    if (result) {
      setInteractions(result.hits ?? []);
      setThereIsMoreContent((result.hits ?? []).length === pageSize);
    }
    setIsLoading(false);
  };

  loadMoreCall.current = async (source, pageNumber) => {
    setIsLoadingScroll(true);
    const requestProperties = { pageNumber, pageSize };
    if (sort) requestProperties.sort = sort;
    if (filtersRef.current?.length) requestProperties.filters = filtersRef.current;
    if (queryRef.current) requestProperties.query = queryRef.current;
    const result = await generalApiCall({
      method: 'post',
      pathname: '/api/crm-interaction/query',
      requestProperties,
      needsAuthentication: true,
      requestSource: source,
      notShowErrorMessage: true,
    });
    if (source.signal.aborted) return;
    if (result) {
      setInteractions((prev) => [...(prev ?? []), ...(result.hits ?? [])]);
      setThereIsMoreContent((result.hits ?? []).length === pageSize);
    }
    setIsLoadingScroll(false);
  };

  const handleScrollBottom = () => {
    if (isLoadingScroll || !thereIsMoreContent) return;
    loadMoreSourceRef.current?.abort();
    loadMoreSourceRef.current = new AbortController();
    pageRef.current += 1;
    loadMoreCall.current(loadMoreSourceRef.current, pageRef.current);
  };

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      setInteractions([]);
      setThereIsMoreContent(false);
      return;
    }
    if (hasUsableCacheRef.current) {
      setIsLoading(false);
      return;
    }
    const source = new AbortController();
    loadMoreSourceRef.current?.abort();
    pageRef.current = 1;
    initialCall.current(source);
    return () => {
      source.abort();
      loadMoreSourceRef.current?.abort();
    };
  }, [dependencyKey, enabled]);

  useEffect(() => {
    if (interactions !== null) onResultChange?.({ hits: interactions, thereIsMoreContent, dependencyKey });
  }, [interactions, thereIsMoreContent]);

  const addInteraction = (newInteraction) => {
    setInteractions((prev) => [newInteraction, ...(prev ?? [])]);
  };

  const updateInteractionLocal = (updatedInteraction) => {
    setInteractions((prev) =>
      (prev ?? []).map((item) => (item.id === updatedInteraction.id ? { ...item, ...updatedInteraction } : item))
    );
  };

  const removeInteraction = (id) => {
    setInteractions((prev) => (prev ?? []).filter((item) => item.id !== id));
  };

  const refetch = () => {
    loadMoreSourceRef.current?.abort();
    pageRef.current = 1;
    initialCall.current(new AbortController());
  };

  return {
    interactions,
    isLoading,
    isLoadingScroll,
    thereIsMoreContent,
    handleScrollBottom,
    addInteraction,
    updateInteractionLocal,
    removeInteraction,
    refetch,
  };
};

export default useCrmInteractionsQuery;
